import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    OnInit,
    Renderer2,
} from '@angular/core';
import { CMPS_DIALOG_DATA, CmpsDialogRef } from '@cmps/ng-uikit';
import { CmpsInsightCoachingPerformanceData, CmpsUser } from 'kit/public-api';
import { tap } from 'rxjs/operators';

import { useSelector2 } from '../../coaching/store-use-selector';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { useDispatcher, UseStore } from '../../store.decorator';
import { reload, selectPillarAction } from '../my-performance-card.action';
import { TranslationKeyMap } from '../my-performance-card.i18n';
import { performanceCardReducer } from '../my-performance-card.reducer';
import { PerformanceCardState } from '../my-performance-card.state';
import { getMetricPerformanceData } from '../state/metric-performance-data.action';
import { togglePillarCollapseAction } from '../state/toggle.action';

@TakeUntilDestroy()
@UseStore<PerformanceCardState>('performanceCard', performanceCardReducer)
@Component({
    selector: 'cmps-pillar-view-dialog',
    templateUrl: './pillar-view-dialog.component.html',
    host: {
        '[class.cmps-pillar-view-dialog--has-id]': '_dialogData?.id != null',
    },
})
export class CmpsPillarViewDialogComponent implements OnInit, AfterViewInit {
    state: PerformanceCardState;
    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _selectedPillarLabel: string;
    /** @private */ _visitedUserIds: number[];
    /** @private */ _visitedUsers: CmpsUser[];
    /** @private */ _currentHistoryIndex;

    constructor(
        public readonly _dialogRef: CmpsDialogRef<
            CmpsPillarViewDialogComponent,
            any
        >,
        @Inject(CMPS_DIALOG_DATA)
        readonly _dialogData: { id: string; cardWidth: number; user: CmpsUser },
        private readonly _renderer: Renderer2,
        private readonly _el: ElementRef<HTMLElement>,
    ) {
        this._currentHistoryIndex = -1;
        this._visitedUserIds = [];
        this._visitedUsers = [];
    }

    ngOnInit(): void {
        const loggedInUser = Object.values(this.state.userData).find(
            (u) => u.isLoggedIn,
        );

        const initialUser = this._dialogData?.user;
        const initialUserId = initialUser?.userId;

        if (
            initialUserId != null &&
            !this._visitedUserIds.includes(initialUserId)
        ) {
            this._visitedUserIds.push(initialUserId);
            this._visitedUsers.push(initialUser);
            this._currentHistoryIndex = this._visitedUserIds.length - 1;
        }

        if (this._dialogData?.id != null) {
            const selectedPillar = loggedInUser?.pillarsData?.find(
                (p) => p.id === this._dialogData.id,
            );
            this._selectedPillarLabel =
                selectedPillar?.parentPillar?.titleLabel ?? '';
            return;
        }

        this._getMetricPerformanceData();

        const userDataExists = this.state.userData?.[initialUserId] != null;

        if (!userDataExists) {
            useDispatcher(
                this,
                reload({
                    userId: initialUserId,
                    triggeredFromRoot: false,
                }),
            );
        }
    }

    ngAfterViewInit(): void {
        this._updateDialogWidth();
    }

    private _updateDialogWidth(): void {
        const dialogElement = this._el.nativeElement.querySelector(
            '.cmps-pillar-view-dialog--dialog',
        );

        if (dialogElement != null) {
            if (
                this._dialogData.id != null &&
                this._dialogData.cardWidth != null
            ) {
                // Apply dynamic width from cardWidth
                this._renderer.setStyle(
                    dialogElement,
                    'width',
                    `${this._dialogData.cardWidth}px`,
                );
            } else {
                // Revert to default width (90vw)
                this._renderer.setStyle(dialogElement, 'width', '90vw');
            }
        }
    }

    /* @private */
    _getMetricPerformanceData(): void {
        useSelector2<boolean>((state) => {
            const performanceCard =
                state?.performanceCard as PerformanceCardState;
            return performanceCard?.pending.reload;
        })
            .pipe(
                tap(([prevState, currState]) => {
                    if (prevState && !currState) {
                        useDispatcher(
                            this,
                            getMetricPerformanceData({
                                userId: this._dialogData.user.userId,
                            }),
                        );
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /* @private */
    _onSelectPillar(
        event: CmpsInsightCoachingPerformanceData,
        isParentPillar: boolean,
    ): void {
        useDispatcher(
            this,
            selectPillarAction({
                userId: this._dialogData.user.userId,
                colorGroup: event.backgroundColor,
                isParentPillar,
            }),
        );
    }
    /* @private */

    _onTogglePillar(id?: string, metricId?: number): void {
        useDispatcher(
            this,
            togglePillarCollapseAction({
                id,
                metricId,
                userId: this._dialogData.user.userId,
            }),
        );
    }
    /* @private */

    _onSelectUser(user?: CmpsUser): void {
        if (user != null) {
            const currentUserId =
                this._visitedUserIds[this._currentHistoryIndex];

            if (currentUserId !== user.userId) {
                this._visitedUserIds.push(user.userId);
                this._visitedUsers.push(user);
                this._currentHistoryIndex = this._visitedUserIds.length - 1;
            }

            this._dialogData.user = user;
            this._dialogData.id = null;

            const userDataExists = this.state.userData?.[user.userId] != null;

            if (!userDataExists) {
                useDispatcher(
                    this,
                    reload({
                        userId: user.userId,
                        triggeredFromRoot: false,
                    }),
                );
            }
            this._updateDialogWidth();
        }
    }

    /** @private */
    _onUserSelect(userId: number): void {
        this._onSelectUser(this.state.users.find((e) => e.userId === userId));
    }
    /* @private */

    _onBackClick(): void {
        if (this._currentHistoryIndex <= 0) {
            return;
        }

        this._currentHistoryIndex--;

        const userId = this._visitedUserIds[this._currentHistoryIndex];
        this._setDialogUserById(userId);
    }
    /* @private */

    _onNextClick(): void {
        if (this._currentHistoryIndex >= this._visitedUserIds.length - 1) {
            return;
        }

        this._currentHistoryIndex++;

        const userId = this._visitedUserIds[this._currentHistoryIndex];
        this._setDialogUserById(userId);
    }

    _onCloseClick(): void {
        this._dialogRef.close();
    }

    private _setDialogUserById(userId: number): void {
        const foundUser = this._visitedUsers.find((u) => u.userId === userId);

        if (foundUser != null) {
            this._dialogData.user = foundUser;
        }
    }
}
